import { PopoverPosition as BlueprintPopoverPosition } from '@blueprintjs/core';
import { Popover2 as BlueprintPopover2 } from '@blueprintjs/popover2';
import classNames from 'classnames';
import * as React from 'react';

import { useNavbarSchema } from 'bones/components/molecules/Navbar/context/NavbarSchema';
import { NavbarMenu } from 'bones/components/molecules/Navbar/navbar/NavbarMenu';
import { NavbarItem } from 'bones/components/molecules/Navbar/types';
import { Button, ButtonProps } from 'bones/design-system/components/Button';
import { Tooltip } from 'bones/design-system/components/Tooltip';
import { Body } from 'bones/design-system/components/typography/Body';

export interface NavbarButtonProps {
  item: NavbarItem;
  variant: ButtonProps['variant'];
}

export const NavbarButton: React.VFC<NavbarButtonProps> = ({
  item,
  variant,
}) => {
  const { expand } = useNavbarSchema();
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="vb-navbar-button-container">
      <BlueprintPopover2
        key={item.key || item.testId}
        content={<NavbarMenu menuItems={item.menuItems} />}
        disabled={!item.menuItems?.length}
        isOpen={isOpen}
        minimal
        onInteraction={(nextIsOpen) => {
          setIsOpen(Boolean(item.menuItems?.length && nextIsOpen));
        }}
        popoverClassName={classNames(
          'vb-navbar-menu-popover',
          `vb-popover-position--${BlueprintPopoverPosition.RIGHT}`
        )}
        portalContainer={
          (process.env.NODE_ENV === 'test' &&
            document.getElementById('vet-bones-root')) ||
          // istanbul ignore next
          undefined
        }
        position={BlueprintPopoverPosition.RIGHT}
        targetProps={{ className: 'vb-navbar-button-popover-target' }}
      >
        <Tooltip
          description={item.label}
          disabled={isOpen || expand.isExpanded}
          position={BlueprintPopoverPosition.RIGHT}
          targetProps={{ className: 'vb-navbar-button-tooltip-target' }}
        >
          <Button
            active={isOpen || item.active}
            badgeProps={item.badgeProps}
            className="vb-navbar-button"
            leftIcon={item.icon}
            onClick={item.onClick}
            size="sm"
            testId={item.testId}
            variant={variant}
          >
            {expand.isExpanded ? (
              <span className="vb-navbar-button-text">{item.label}</span>
            ) : null}
          </Button>
        </Tooltip>
      </BlueprintPopover2>
      {item.subItems?.length ? (
        <div className="vb-navbar-subitems">
          <div className="vb-navbar-subitem">
            <div
              className={classNames(
                'vb-navbar-subitem-indicator',
                'vb-navbar-subitem-indicator--label'
              )}
            >
              <div className="vb-navbar-subitem-indicator--top" />
              <div className="vb-navbar-subitem-indicator--bottom" />
            </div>

            <Body className="vb-navbar-subitem-label" size="xs">
              {item.subItemsLabel}
            </Body>
          </div>
          {item.subItems.map((subItem) => (
            <div
              key={subItem.key || subItem.testId}
              className="vb-navbar-subitem"
            >
              <div
                className={classNames(
                  'vb-navbar-subitem-indicator',
                  'vb-navbar-subitem-indicator--button'
                )}
              >
                <div className="vb-navbar-subitem-indicator--top" />
                <div className="vb-navbar-subitem-indicator--bottom" />
              </div>

              <Button
                active={isOpen || subItem.active}
                className="vb-navbar-subitem-button"
                onClick={subItem.onClick}
                size="xs"
                testId={subItem.testId}
                variant="dark-ghost-gray"
              >
                {subItem.label}
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
